<template>
    <div class="loader-wrapper">
        <div class="loader"
             :class="`${variant}-variant`"
             :style="loaderStyle" />
        <div v-if="text"
             class="loader-text"
             :style="textStyle">
            {{ text }}
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { LoaderVariantsEnum } from '../enums/loaderVariants';
    import { resolveCssValue } from '../helpers/resolveCssValue';

    interface Props {
        variant?: LoaderVariantsEnum;
        size?: string | number;
        thickness?: string | number;
        text?: string;
        fontSize?: string | number;
    }
    const props = withDefaults(defineProps<Props>(), {
        variant: LoaderVariantsEnum.PURPLE,
        size: 24,
        thickness: 4,
        text: '',
        fontSize: 12,
    });

    const loaderStyle = computed(() => ({
        width: resolveCssValue(props.size),
        height: resolveCssValue(props.size),
        padding: resolveCssValue(props.thickness),
    }));

    const textStyle = computed(() => ({
        fontSize: resolveCssValue(props.fontSize),
    }));
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;

@function loaderBackground($color) {
    @return conic-gradient(transparent 0% 15%, $color);
}

.loader-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.loader {
    border-radius: 100%;
    animation: spin 400ms infinite linear;
    aspect-ratio: 1;
    mask: conic-gradient(legacyVariables.$black, legacyVariables.$black), linear-gradient(legacyVariables.$black 0 0) content-box;
    mask-composite: subtract;

    &.purple-variant {
        background: loaderBackground(legacyVariables.$purple);
    }

    &.dark-purple-variant {
        background: loaderBackground(legacyVariables.$indigo);
    }

    &.light-purple-variant {
        background: loaderBackground(legacyVariables.$soft-purple);
    }

    &.black-variant {
        background: loaderBackground(legacyVariables.$black);
    }

    &.white-variant {
        background: loaderBackground(legacyVariables.$white);
    }
}

.loader-text {
    margin-top: 8px;
    color: legacyVariables.$black;
}

.dark-theme {
    .loader-text {
        color: legacyVariables.$white;
    }
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
